import { useEffect, useRef, useState, useCallback } from 'react';
import { ordersService } from '../services/ordersService';
import { fingerprintQuoteRequest } from '../utils/storefrontOrder/payload';

const QUOTE_DEBOUNCE_MS = 250;

// Quotes an order request with debouncing, a fingerprint cache, and
// stale-response protection. Pass a null request to go idle.
export function useOrderQuote(request) {
  const [quoteState, setQuoteState] = useState({ quote: null, fingerprint: null });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  const requestCounterRef = useRef(0);
  const lastFingerprintRef = useRef(null);
  const quoteCacheRef = useRef(new Map());

  const runQuote = useCallback(async (currentRequest, { bypassCache = false } = {}) => {
    const requestId = ++requestCounterRef.current;

    if (!currentRequest) {
      setStatus('idle');
      setQuoteState({ quote: null, fingerprint: null });
      setError(null);
      return null;
    }

    const fingerprint = fingerprintQuoteRequest(currentRequest);

    if (!bypassCache && quoteCacheRef.current.has(fingerprint)) {
      const cached = quoteCacheRef.current.get(fingerprint);
      setQuoteState({ quote: cached, fingerprint });
      setError(null);
      setStatus('ready');
      return cached;
    }

    setStatus('loading');
    setError(null);

    try {
      const result = await ordersService.calculateStorefrontQuote(currentRequest);

      // Discard responses for requests that are no longer current.
      if (requestId !== requestCounterRef.current) {
        return null;
      }

      quoteCacheRef.current.set(fingerprint, result);
      setQuoteState({ quote: result, fingerprint });
      setError(null);
      setStatus('ready');
      return result;
    } catch (err) {
      if (requestId !== requestCounterRef.current) {
        return null;
      }
      setError(err.message || 'Unable to calculate order pricing');
      setStatus('error');
      throw err;
    }
  }, []);

  useEffect(() => {
    // Supersede in-flight responses as soon as the request changes, not only
    // when the debounce fires, so an old response is never exposed as current.
    const fingerprint = request ? fingerprintQuoteRequest(request) : null;
    if (fingerprint !== lastFingerprintRef.current) {
      lastFingerprintRef.current = fingerprint;
      requestCounterRef.current += 1;
    }

    if (!request) {
      // Invalidate any in-flight response and go idle.
      runQuote(null).catch(() => { });
      return undefined;
    }

    const timer = setTimeout(() => {
      runQuote(request).catch(() => {
        // State already reflects the error.
      });
    }, QUOTE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [request, runQuote]);

  const refresh = useCallback(
    () => runQuote(request, { bypassCache: true }),
    [request, runQuote]
  );

  return {
    quote: quoteState.quote,
    quoteFingerprint: quoteState.quote ? quoteState.fingerprint : null,
    status,
    error,
    refresh
  };
}
